import { Suspense, lazy, useMemo, useRef } from "preact/compat";
import type { LoaderFunctionArgs } from "react-router";
import { Outlet, RouterProvider, createBrowserRouter } from "react-router";
import Header from "~/components/header.tsx";
import Footer from "~/components/footer.tsx";
import {
	isSignedInAuthenticationResult,
	useAuthenticationState,
	isLoadingAuthenticationResult,
	isSignedOutAuthenticationResult,
} from "~/hooks/auth-state.tsx";
import LoadingSpinner from "~/components/common/loading-spinner.tsx";

const Home = lazy(() => import("~/features/home/home.tsx"));
const CompletedDesigns = lazy(
	() => import("~/features/user/completed-designs.tsx"),
);
const SavedDesigns = lazy(() => import("~/features/user/saved-designs.tsx"));
const Profile = lazy(() => import("~/features/user/profile.tsx"));
const VerifyEmail = lazy(() => import("~/features/user/verify-email.tsx"));

const Login = lazy(() => import("~/features/auth/login.tsx"));
const SignUp = lazy(() => import("~/features/auth/sign-up.tsx"));
const ConfirmSignUp = lazy(() => import("~/features/auth/confirm-sign-up.tsx"));

const RouterErrorPage = lazy(() => import("~/features/error/router-error.tsx"));

function App() {
	const authResult = useAuthenticationState();
	const authResultRef = useRef(authResult);
	authResultRef.current = authResult;

	// Makes a white flash
	if (isLoadingAuthenticationResult(authResult)) {
		return (
			<div
				style={{
					padding: 30,
					marginLeft: "auto",
					marginRight: "auto",
				}}
			>
				<LoadingSpinner />
			</div>
		);
	}

	const router = useMemo(() => {
		function signedInOnlyLoader({ request }: LoaderFunctionArgs) {
			if (!isSignedInAuthenticationResult(authResultRef.current)) {
				const searchParams = new URLSearchParams({
					redirectTo: new URL(request.url).pathname,
				});
				// eslint-disable-next-line @typescript-eslint/only-throw-error
				throw new Response(null, {
					status: 307,
					headers: {
						Location: `/login/?${searchParams}`,
					},
				});
			}
			return null;
		}

		function signedOutOnlyLoader() {
			if (!isSignedOutAuthenticationResult(authResultRef.current)) {
				// eslint-disable-next-line @typescript-eslint/only-throw-error
				throw new Response(null, {
					status: 307,
					headers: {
						Location: `/profile/`,
					},
				});
			}
			return null;
		}

		return createBrowserRouter([
			{
				element: (
					<Suspense fallback={<LoadingSpinner />}>
						<Outlet />
					</Suspense>
				),
				errorElement: (
					<Suspense fallback={<LoadingSpinner />}>
						<RouterErrorPage />
					</Suspense>
				),
				children: [
					{
						element: (
							<>
								<Header />
								<Outlet />
								<Footer />
							</>
						),
						children: [
							{ index: true, element: <Home /> },

							// Public routes (no auth required)
							{
								path: "verify-email/:token",
								element: <VerifyEmail />,
							},

							// Signed in only
							{
								path: "saved-designs",
								loader: signedInOnlyLoader,
								element: <SavedDesigns />,
							},
							{
								path: "completed-designs",
								loader: signedInOnlyLoader,
								element: <CompletedDesigns />,
							},
							{
								path: "profile",
								loader: signedInOnlyLoader,
								element: <Profile />,
							},

							// Signed out only
							{
								path: "sign-up",
								loader: signedOutOnlyLoader,
								element: <SignUp />,
							},
							{
								path: "confirm-sign-up/:emailAddress",
								loader: signedOutOnlyLoader,
								element: <ConfirmSignUp />,
							},
							{
								path: "login",
								loader: signedOutOnlyLoader,
								element: <Login />,
							},
						],
					},
				],
			},
		]);
	}, [authResultRef]);

	return <RouterProvider router={router} />;
}

export default App;
